"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import {
  TableCell,
  TableRow,
  Typography,
  Button,
  Box,
  IconButton,
  CircularProgress,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  calculateNetTotal,
  calculateUpdatedDiscountPercentage,
  cartPriceType,
  formatWithCommasWithDecimal,
  getCurrentQuantity,
  getNDPPriceAndQty,
  handleAddToCart,
  handleRemoveFromCart,
  isInBulkCart,
} from "@/utils/helpers";
import { HomeProduct } from "@/types/home";
import ProductDetail from "./ProductDetail";
import { useAppDispatch, useAppSelector } from "@/hooks/useAppSelector";
import { AnimatedQuantity, FormattedNumber } from "@/utils/numberFlow";
import { LoaderState } from "@/types/cart";
import gsap from "gsap";
import { setSelectedSKU } from "@/store/slices/homeSlice";
import { trackEvent } from "@/lib/gtag";
import PriceCell from "./PriceCell";
import debounce from "lodash.debounce";

const IMAGE_BASE = process.env.NEXT_PUBLIC_IMAGE_BASE;

interface ProductRowProps {
  data: HomeProduct;
}

const ProductRow: React.FC<ProductRowProps> = ({ data }) => {
  const dispatch = useAppDispatch();
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [open, setOpen] = useState<LoaderState | null>(null);
  const [inputQty, setInputQty] = useState<string>("");

  const { phoneNumber } = useAppSelector((state) => state.auth);
  const { SKU_Details } = useAppSelector((state) => state.home);
  const { cart } = useAppSelector((state) => state.cart);
  const [selected, setSelected] = useState<"ndp" | "sales">(
    cartPriceType(cart, data?.SKU_ID)
  );

  const redixData = getNDPPriceAndQty(SKU_Details, data?.SKU_ID);
  const currentQuantity = getCurrentQuantity(data?.SKU_ID, cart) || 0;

  useEffect(() => {
    if (selected === "ndp") {
      setInputQty(currentQuantity.toString());
    }
  }, [selected, currentQuantity]);

  const debouncedUpdateCart = debounce((qty: number) => {
    handleAddToCart(
      dispatch,
      data?.SKU_ID,
      phoneNumber,
      qty,
      selected,
      handleClose
    );
  }, 500);

  const handleOpen = (sku: string, type: string) =>
    setOpen({ sku: sku, type: type });
  const handleClose = () => setOpen(null);

  const openSidebar = () => {
    setIsSidebarOpen(true);
    dispatch(setSelectedSKU(data?.SKU_ID));
    trackEvent({
      action: "Open Sidebar",
      category: "Product",
      label: data?.SKU_ID,
    });
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
    dispatch(setSelectedSKU(null));
  };

  const handleInputQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    const availableQty = redixData?.qty ?? 0;
    if (availableQty > Number(value)) {
      setInputQty(value);
    } else {
      setInputQty(String(availableQty));
    }
    const numericValue =
      availableQty > Number(value) ? parseInt(value) : availableQty;
    if (!isNaN(numericValue)) debouncedUpdateCart(numericValue);
  };

  const handleAddCarts = (type: string) => {
    const bundleQty = parseInt(data?.Bundle) || 1;
    const availableQty = redixData?.qty ?? 0;

    let newQty = currentQuantity;

    if (type === "inc") {
      if (availableQty <= currentQuantity) {
        return;
      }

      if (selected === "ndp") {
        if (newQty + 1 <= availableQty) {
          newQty += 1;
        } else {
          return;
        }
      } else if (selected === "sales") {
        const remainder = currentQuantity % bundleQty;
        const increment = remainder === 0 ? bundleQty : bundleQty - remainder;
        if (newQty + increment <= availableQty) {
          newQty += increment;
        } else {
          return;
        }
      } else {
        // default case (e.g., wholesale)
        if (newQty + bundleQty <= availableQty) {
          newQty += bundleQty;
        } else {
          return;
        }
      }
    }

    if (type === "dec") {
      if (selected === "ndp") {
        newQty = Math.max(0, newQty - 1);
      } else if (selected === "sales") {
        const remainder = newQty % bundleQty;
        newQty =
          remainder === 0
            ? Math.max(0, newQty - bundleQty)
            : Math.max(0, newQty - remainder);
      } else {
        newQty = Math.max(0, newQty - bundleQty);
      }
    }

    // Avoid adding 0 quantity
    if (newQty === 0 && type === "dec") {
      return;
    }

    trackEvent({
      action: type === "inc" ? "Increase Quantity" : "Decrease Quantity",
      category: "Product",
      label: data?.SKU_ID,
    });

    handleOpen(data?.SKU_ID, type);

    handleAddToCart(
      dispatch,
      data?.SKU_ID,
      phoneNumber,
      newQty,
      selected,
      handleClose
    );
  };

  const handleRemove = async () => {
    if (!isInBulkCart(cart, data?.SKU_ID)) return;

    await handleOpen(data?.SKU_ID, "refresh");

    trackEvent({
      action: "Remove Product",
      category: "Product",
      label: data?.SKU_ID,
    });

    if (open?.type === "refresh") {
      await gsap.to("#refresh-icon", {
        rotation: 360,
        repeat: -1,
        duration: 1,
        ease: "linear",
      });
    }

    await handleRemoveFromCart(
      dispatch,
      data?.SKU_ID,
      phoneNumber,
      handleClose
    );

    if (open === null) {
      gsap.killTweensOf("#refresh-icon");
      gsap.to("#refresh-icon", { rotation: 0, duration: 0.3 });
    }
  };

  return (
    <TableRow
      sx={{
        "&:first-child td": { borderTop: "none" },
        "&:last-child td": { borderBottomWidth: "2px" },
      }}
    >
      <TableCell
        className="border border-gray-300 w-[100px] !font-bold !font-[Raleway]"
        align="center"
      >
        {data.Bar_Code}
      </TableCell>
      <TableCell
        className="border !p-0 border-gray-300 w-[150px] cursor-pointer relative group open-sidebar"
        align="center"
        onClick={() => openSidebar()}
      >
        <div className="flex items-center justify-center h-[80px] w-full">
          <Image
            quality={50}
            property="no"
            unoptimized
            src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${IMAGE_BASE}_PRODUCTS_IMAGE/${data.Image?.[0]}`}
            alt="Product"
            width={80}
            height={80}
            className="w-auto min-w-[120px] h-auto max-w-full max-h-full object-contain transition-transform duration-200 group-hover:scale-105"
          />
        </div>
        {/* Eye Icon on Hover */}
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black-1 bg-opacity-50 p-1 rounded-full">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="white"
            className="w-3 h-3"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 5C7.029 5 3 9 3 12s4.029 7 9 7 9-4 9-7-4.029-7-9-7zm0 0v14m0-14a5 5 0 00-5 5m5-5a5 5 0 015 5"
            />
          </svg>
        </div>
      </TableCell>

      <TableCell
        className="border border-gray-300 w-[400px] !font-bold !font-[Raleway] cursor-pointer"
        align="left"
        onClick={() => openSidebar()}
        sx={{ textTransform: "none", fontSize: "15px" }}
      >
        {data?.prod_name?.trim()}

        {/* Check if Tech_Data (string) exists, else fallback to ProductTechAttributes */}
        {(data?.Tech_Data?.trim() ||
          data?.ProductTechAttributes?.filter(Boolean).length > 0) && (
          <span className="text-gray-500">&nbsp;&nbsp;| </span>
        )}

        {data?.Tech_Data?.trim() ? (
          <span className="ml-1 text-gray-500 text-[12px]">
            {data?.Tech_Data}
          </span>
        ) : (
          data?.ProductTechAttributes?.filter(Boolean).map(
            (tech, index, arr) => (
              <span key={index} className="ml-1 text-gray-500 text-[12px]">
                {tech}
                {index !== arr?.length - 1 && ", "}
              </span>
            )
          )
        )}
      </TableCell>

      <PriceCell
        data={data}
        redixData={redixData}
        selected={selected}
        setSelected={setSelected}
      />

      <TableCell
        className="border  relative border-gray-300 !font-semibold !font-[Raleway]"
        align="center"
      >
        ₹{formatWithCommasWithDecimal(Number(data?.MRP_price))}
        {/* Est. Margin - show only if no Sales_Price */}
        {calculateUpdatedDiscountPercentage(
          Number(data?.MRP_price),
          selected === "sales" && Number(data?.Sales_Price) > 0
            ? Number(data?.Sales_Price)
            : Number(data?.NDP_price),
          data?.GST
        ) > 2 && (
          <Typography
            position="absolute"
            left="50%"
            sx={{ transform: "translateX(-50%)", top: "70%" }}
            fontSize="10px"
            bgcolor="var(--color-peach)"
            whiteSpace="nowrap"
            borderRadius="4px"
            padding="3px 5px"
            color="white"
            className="!font-[Raleway]"
          >
            Est. margin:{" "}
            <b>
              <AnimatedQuantity
                value={calculateUpdatedDiscountPercentage(
                  Number(data?.MRP_price),
                  selected === "sales" && Number(data?.Sales_Price) > 0
                    ? Number(data?.Sales_Price)
                    : Number(data?.NDP_price),
                  data?.GST
                )}
              />
              %
            </b>
          </Typography>
        )}
      </TableCell>
      <TableCell
        className="border border-gray-300 !font-[Raleway]"
        align="center"
        sx={{ position: "relative", width: "220px" }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          marginLeft="15px"
        >
          <Button
            variant="contained"
            size="small"
            sx={{
              width: "40px",
              height: "35px",
              minWidth: "unset",
              fontSize: "25px",
              display: "flex",
              boxShadow: "none",
              alignItems: "center",
              borderRadius: "5px 0 0 5px",
              justifyContent: "center",
              backgroundColor:
                currentQuantity > 0 ? "var(--color-green)" : "#a2deb1",
              color: "#fff",
              "&:hover": {
                boxShadow: "none",
                backgroundColor:
                  currentQuantity > 0 ? "var(--color-green-hover)" : "#a2deb1",
              },
              cursor: currentQuantity > 0 ? "pointer" : "not-allowed",
            }}
            onClick={() => {
              if (selected === "ndp") {
                if (currentQuantity === 1) {
                  handleOpen(data?.SKU_ID, "dec");
                  handleRemoveFromCart(
                    dispatch,
                    data?.SKU_ID,
                    phoneNumber,
                    handleClose
                  );
                } else {
                  handleAddCarts("dec");
                }
              } else {
                if (currentQuantity === Number(data?.Bundle)) {
                  handleOpen(data?.SKU_ID, "dec");
                  handleRemoveFromCart(
                    dispatch,
                    data?.SKU_ID,
                    phoneNumber,
                    handleClose
                  );
                } else {
                  handleAddCarts("dec");
                }
              }
            }}
          >
            {open?.sku === data?.SKU_ID && open?.type === "dec" ? (
              <CircularProgress size={10} sx={{ color: "white" }} />
            ) : (
              "-"
            )}
          </Button>

          {/* Quantity Display */}
          <Box
            display="flex"
            alignItems="center"
            bgcolor="#f7f7f7"
            height={35}
            width={50}
            justifyContent="center"
          >
            {selected === "ndp" ? (
              <TextField
                variant="standard"
                value={inputQty}
                onChange={handleInputQtyChange}
                inputProps={{
                  style: {
                    textAlign: "center",
                    fontSize: "12px",
                    padding: 0,
                    height: 35,
                    width: 50,
                    fontFamily: "Raleway",
                  },
                }}
                InputProps={{
                  disableUnderline: true,
                  style: {
                    height: 35,
                    width: 50,
                    textAlign: "center",
                    justifyContent: "center",
                    display: "flex",
                  },
                }}
              />
            ) : (
              <Typography
                variant="subtitle1"
                fontSize={12}
                width="100%"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  fontFamily: "Raleway",
                }}
              >
                <AnimatedQuantity
                  value={isInBulkCart(cart, data?.SKU_ID) ? currentQuantity : 0}
                />
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            size="small"
            sx={{
              width: "40px",
              height: "35px",
              minWidth: "unset",
              fontSize: "25px",
              borderRadius: "0 5px 5px 0",
              boxShadow: "none",
              backgroundColor:
                ((selected === "sales" && redixData?.qty >= Number(data?.Bundle)) && currentQuantity < redixData?.qty) || (selected === "ndp" && redixData?.qty > 0 && currentQuantity < redixData?.qty)
                  ? "var(--color-green)"
                  : "#a2deb1",
              color: "#fff",
              "&:hover": {
                boxShadow: "none",
                backgroundColor:
                   ((selected === "sales" && redixData?.qty >= Number(data?.Bundle)) && currentQuantity < redixData?.qty) || (selected === "ndp" && redixData?.qty > 0 && currentQuantity < redixData?.qty)
                    ? "var(--color-green-hover)"
                    : "#a2deb1",
              },
              cursor:
                 ((selected === "sales" && redixData?.qty >= Number(data?.Bundle)) && currentQuantity < redixData?.qty) || (selected === "ndp" && redixData?.qty > 0 && currentQuantity < redixData?.qty)
                  ? "pointer"
                  : "not-allowed",
            }}
            onClick={() => {
              if (currentQuantity === 0) {
                handleAddCarts("inc"); // Add the product to the bulk cart when the quantity is 0
              } else if ( ((selected === "sales" && redixData?.qty >= Number(data?.Bundle)) && currentQuantity < redixData?.qty) || (selected === "ndp" && redixData?.qty > 0 && currentQuantity < redixData?.qty)) {
                handleAddCarts("inc"); // Increase the quantity if it's less than available
              }
            }}
            // disabled={currentQuantity >= (redixData?.qty ?? 0 )}
          >
            {open?.sku === data?.SKU_ID &&
            (open?.type === "inc" || open?.type === "no") ? (
              <CircularProgress size={10} sx={{ color: "white" }} />
            ) : (
              "+"
            )}
          </Button>

          <IconButton
            sx={{
              color: "var(--color-gray)",
              marginLeft: "0",
              "&:hover": {
                color: "gray",
              },
            }}
            aria-label="Reset quantity"
            onClick={handleRemove}
            disabled={open?.type === "refresh"}
          >
            <CloseIcon
              id="refresh-icon"
              className={`${
                currentQuantity > 0
                  ? "text-peach cursor-pointer"
                  : "text-peach/30 cursor-not-allowed"
              }`}
              style={{ fontSize: "15px" }}
            />
          </IconButton>
        </Box>
        {selected === "sales" && (
          <Box
            sx={{
              width: "100%",
              fontSize: "10px",
              color: "gray",
              fontWeight: "700",
              position: "absolute",
              bottom: "10%",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <strong>Note:</strong> Increase as pack of {data?.Bundle} items
          </Box>
        )}
      </TableCell>

      <TableCell
        className="border border-gray-300 w-[280px] !font-[Raleway]"
        align="center"
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography align="left" fontSize="12px">
              Dealer Price:
            </Typography>

            <Typography align="right" fontSize="12px">
              <FormattedNumber
                value={
                  currentQuantity &&
                  selected === "sales" &&
                  data?.Sales_Qunatity !== null &&
                  data?.Sales_Qunatity !== 0 &&
                  data?.Sales_Price !== null &&
                  Number(data?.Sales_Price) !== 0
                    ? Number(data?.Sales_Price) * currentQuantity
                    : Number(redixData?.NDP_price) * currentQuantity
                }
              />
            </Typography>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography align="left" fontSize="12px">
              GST:
            </Typography>
            <Typography align="right" fontSize="12px">
              {data?.GST}%
            </Typography>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              fontWeight="bold"
              align="left"
              fontSize="13px"
              className="!font-[Raleway]"
            >
              Net Total:
            </Typography>
            <Typography
              fontWeight="bold"
              align="right"
              fontSize="13px"
              className="!font-[Raleway]"
            >
              <FormattedNumber
                value={calculateNetTotal(
                  currentQuantity &&
                    selected === "sales" &&
                    data?.Sales_Qunatity !== null &&
                    data?.Sales_Qunatity !== 0 &&
                    data?.Sales_Price !== null &&
                    Number(data?.Sales_Price) !== 0
                    ? String(data?.Sales_Price)
                    : String(redixData?.NDP_price),
                  data?.GST,
                  currentQuantity
                )}
              />
            </Typography>
          </Box>
        </Box>
      </TableCell>
      {isSidebarOpen && (
        <ProductDetail
          isOpen={isSidebarOpen}
          onClose={closeSidebar}
          product={data}
          selected={selected}
        />
      )}
    </TableRow>
  );
};

export default React.memo(ProductRow);
